// SignupStep5.jsx — Basics & Lifestyle (DOB, Marital Status, Height, Weight, Grew Up In, Diet, Blood Group, Disability)
import React from "react";
import { MARITAL_STATUS, HEIGHTS, DIETS, COUNTRIES } from "../../constants/formData";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Prefer not to say"];

function normalizeOptions(list) {
  if (!Array.isArray(list)) return [];
  return list.map((entry) =>
    typeof entry === "string" ? { value: entry, label: entry } : entry
  );
}

function calculateAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

const maritalOptions = normalizeOptions(MARITAL_STATUS);
const heightOptions = normalizeOptions(HEIGHTS);
const dietOptions = normalizeOptions(DIETS);
const countryOptions = normalizeOptions(COUNTRIES);

const Step5 = ({ formData, setFormData, prevStep, nextStep }) => {
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // NEW — weight. Digits only (and a single decimal point, e.g. "68.5"),
  // stripped as the user types rather than validated after the fact, so
  // there's never a moment where an invalid character sits in the field.
  const handleWeightChange = (e) => {
    const cleaned = e.target.value
      .replace(/[^0-9.]/g, "")
      .replace(/(\..*)\./g, "$1"); // keep only the first decimal point
    setFormData({ ...formData, weight: cleaned });
  };

  const age = calculateAge(formData.dob);

  // Everything on this step is optional (see authValidator.js's
  // validateRegister — dob/maritalStatus/height/weight/grewUpIn/diet/
  // bloodGroup/disability are all .optional()), so Continue is always
  // available.

  return (
    <div className="step5">
      <div className="left-icon1">
        <button type="button" className="backbutton" onClick={prevStep}>
          <i className="fa fa-long-arrow-left" aria-hidden="true"></i>
        </button>
      </div>
      <div className="icon-blk text-center">
        <img src="images/profileform.png" alt="Basics & Lifestyle" />
      </div>
      <div className="form-basic">
        <h5 className="modal-title">Basics &amp; Lifestyle</h5>
        <p className="text-center" style={{ fontSize: 12, color: "#888", marginTop: -6 }}>
          Optional — you can fill these in later from your profile.
        </p>

        <div className="mb-3">
          <label className="form-label" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
            Date of birth {age !== null && <span style={{ color: "#888" }}>(Age: {age})</span>}
          </label>
          <input
            type="date"
            className="form-control"
            name="dob"
            value={formData.dob}
            onChange={handleChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Marital Status</label>
          <div className="select-control">
            <select className="form-control" name="maritalStatus" value={formData.maritalStatus} onChange={handleChange}>
              <option value="">Select</option>
              {maritalOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Height</label>
          <div className="select-control">
            <select className="form-control" name="height" value={formData.height} onChange={handleChange}>
              <option value="">Select</option>
              {heightOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* NEW — Weight, numeric-only input with a "kg" suffix on the right */}
        <div className="mb-3">
          <label className="form-label" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Weight</label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              inputMode="decimal"
              className="form-control"
              name="weight"
              value={formData.weight || ""}
              onChange={handleWeightChange}
              placeholder="e.g. 68"
              style={{ paddingRight: 42 }}
            />
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: 14,
                transform: "translateY(-50%)",
                color: "#888780",
                fontSize: 13,
                pointerEvents: "none",
              }}
            >
              kg
            </span>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Grew up in</label>
          <div className="select-control">
            <select className="form-control" name="grewUpIn" value={formData.grewUpIn} onChange={handleChange}>
              <option value="">Select</option>
              {countryOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Diet</label>
          <div className="toggle-group">
            {dietOptions.map((o) => (
              <label key={o.value} className="custom-radio">
                <input
                  type="radio"
                  name="diet"
                  value={o.value}
                  checked={formData.diet === o.value}
                  onChange={handleChange}
                />
                <span className="switch-label">{o.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Blood Group</label>
          <div className="select-control">
            <select className="form-control" name="bloodGroup" value={formData.bloodGroup} onChange={handleChange}>
              <option value="">Select</option>
              {BLOOD_GROUPS.map((bg) => (
                <option key={bg} value={bg}>{bg}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Disability</label>
          <div className="toggle-group">
            {["No", "Yes"].map((val) => (
              <label key={val} className="custom-radio">
                <input
                  type="radio"
                  name="disability"
                  value={val}
                  checked={formData.disability === val}
                  onChange={handleChange}
                />
                <span className="switch-label">{val}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="modal-footer border-0 justify-content-center p-0 footer-modal mt-4">
          <button type="button" className="py-2 btn-filled" onClick={nextStep}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step5;